import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { Loader } from '../loader/loader';

// URLs accepted by IframeModal — extend only when adding a new trusted embed source.
const ALLOWED_HOSTS = ['www.surveymonkey.com', 'de.surveymonkey.com', 'surveymonkey.com'];

@Component({
  selector: 'app-iframe-modal',
  standalone: true,
  imports: [Loader],
  templateUrl: './iframe-modal.html',
  styleUrl: './iframe-modal.scss',
})
export class IframeModal implements AfterViewInit {
  @ViewChild('frame') private frameRef!: ElementRef<HTMLIFrameElement>;
  // Validated URL, set via ngAfterViewInit so the iframe enters the DOM as about:blank first.
  private validatedUrl: string | null = null;

  @Input() set url(value: string) {
    let parsed: URL;
    try {
      parsed = new URL(value);
    } catch {
      console.error('IframeModal: invalid URL');
      this.isLoading.set(false);
      return;
    }
    if (parsed.protocol !== 'https:' || !ALLOWED_HOSTS.includes(parsed.hostname)) {
      console.error('IframeModal: URL not in allowlist');
      this.isLoading.set(false);
      return;
    }
    this.validatedUrl = value;
  }
  @Output() closed = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();

  readonly isLoading = signal(true);
  // Tracks iframe (load) events:
  //   1 = about:blank initial doc (skip)
  //   2 = survey page loaded
  //   3+ = post-submit redirect (survey-taken) → completion
  private loadCount = 0;

  constructor() {
    document.body.style.overflow = 'hidden';
    inject(DestroyRef).onDestroy(() => {
      document.body.style.overflow = '';
    });
  }

  ngAfterViewInit(): void {
    if (this.validatedUrl) {
      // Setting src after the iframe is already in the DOM (as about:blank) guarantees
      // the browser fires the about:blank load event before navigating, keeping the
      // loadCount logic reliable regardless of Angular rendering order.
      this.frameRef.nativeElement.src = this.validatedUrl;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }

  close(): void {
    this.closed.emit();
  }

  onFrameLoad(): void {
    this.loadCount++;
    if (this.loadCount === 1) return; // about:blank
    if (this.loadCount === 2) {
      this.isLoading.set(false);
      return;
    }
    // Third load = post-submit redirect
    this.completed.emit();
    this.close();
  }
}
